import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });

      if (response.data.token) {
        localStorage.setItem("admin_token", response.data.token);
        localStorage.setItem("admin_email", response.data.email);
        toast.success("Giriş başarılı!");
        navigate("/admin/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(
        error.response?.data?.detail || "Giriş başarısız. Lütfen tekrar deneyin."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen animated-gradient relative flex items-center justify-center px-4 overflow-hidden">
      {/* Simplified Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{willChange: 'transform'}}>
        {/* Static glow effects */}
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 right-1/3 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-md w-full relative z-10">
        {/* Logo with ultra modern effects */}
        <div className="text-center mb-10">
          <div className="logo-container inline-block mb-6">
            <div className="logo-ring"></div>
            <div className="logo-ring"></div>
            <img 
              src="/images/alcodot-logo.png" 
              alt="Alcodot Logo" 
              className="logo-image h-20 w-auto"
            />
          </div>
          <p className="text-xl text-teal-400 font-bold">Admin Panel</p>
        </div>

        {/* Login Form */}
        <div className="glass-card-premium rounded-3xl p-10 shadow-2xl neon-border hover-lift card-3d">
          <form onSubmit={handleSubmit} className="space-y-7">
            <div className="space-y-3">
              <Label htmlFor="email" className="text-sm font-bold text-teal-400 uppercase tracking-wider">
                E-posta
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@alcodot.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg placeholder:text-gray-600 focus:border-teal-500"
                data-testid="admin-email-input"
              />
            </div>

            <div className="space-y-3">
              <Label htmlFor="password" className="text-sm font-bold text-teal-400 uppercase tracking-wider">
                Şifre
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-14 rounded-2xl bg-gray-900/50 border-2 border-teal-500/30 text-white text-lg placeholder:text-gray-600 focus:border-teal-500"
                data-testid="admin-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-16 rounded-2xl bg-gradient-to-r from-teal-500 via-cyan-500 to-sky-500 hover:from-teal-400 hover:via-cyan-400 hover:to-sky-400 text-black font-black text-xl shadow-lg neon-glow smooth-transition shimmer"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-6 h-6 mr-3 animate-spin" />
                  Giriş Yapılıyor...
                </>
              ) : (
                <>
                  <Lock className="w-6 h-6 mr-3" />
                  Giriş Yap
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <a
            href="/"
            className="text-teal-400 hover:text-teal-300 smooth-transition text-sm font-semibold hover:drop-shadow-[0_0_10px_rgba(20,184,166,0.6)]"
            data-testid="back-home-link"
          >
            ← Ana sayfaya dön
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;